// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import type {TerminalContext} from 'docs/types';

import ChipperPhoto from './reader-images/bbpos-photo.png';
import WisePad3Photo from './reader-images/wisepad-3-full.png';
import WisePOSEPhoto from './reader-images/wisepos-e-full.png';
import StripeM2Photo from './reader-images/stripe-m2-full.png';

const messages = defineMessages({
  chipperName: {
    id: 'docsShell.ReaderDetails.chipper.name',
    description: 'Reader chipper. Name',
    defaultMessage: 'BBPOS CHIPPER™ 2X BT',
  },
  chipperDescription: {
    id: 'docsShell.ReaderDetails.chipper.description',
    description: 'Reader chipper. Description',
    defaultMessage:
      'The handheld, Bluetooth reader that connects to iOS and Android applications',
  },
  chipperBadgeLabel: {
    id: 'docsShell.ReaderDetails.chipper.badge.label',
    description: 'Reader chipper. Badge. Label',
    defaultMessage: 'U.S. Only',
  },
  chipperFeature1: {
    id: 'docsShell.ReaderDetails.chipper.feature1',
    description: 'Reader chipper. Feature #1',
    defaultMessage:
      'Create a roaming cashier experience, where customers can pay anywhere',
  },
  chipperFeature2: {
    id: 'docsShell.ReaderDetails.chipper.feature2',
    description: 'Reader chipper. Feature #2',
    defaultMessage: 'Works with iOS and Android SDKs',
  },
  chipperFeature3: {
    id: 'docsShell.ReaderDetails.chipper.feature3',
    description: 'Reader chipper. Feature #3',
    defaultMessage: 'Uses Bluetooth, optimized for mobile checkout experiences',
  },
  chipperFeature4: {
    id: 'docsShell.ReaderDetails.chipper.feature4',
    description: 'Reader chipper. Feature #4',
    defaultMessage: 'Accepts contactless, chip, and swipe payments',
  },
  stripem2Name: {
    id: 'docsShell.ReaderDetails.stripem2.name',
    description: 'Reader stripem2. Name',
    defaultMessage: 'Stripe Reader M2',
  },
  stripem2Description: {
    id: 'docsShell.ReaderDetails.stripem2.description',
    description: 'Reader stripem2. Description',
    defaultMessage:
      'The handheld, Bluetooth reader that connects to iOS and Android applications',
  },
  stripem2BadgeLabel: {
    id: 'docsShell.ReaderDetails.stripem2.badge.label',
    description: 'Reader stripem2. Badge. Label',
    defaultMessage: 'U.S. Only',
  },
  stripem2Feature1: {
    id: 'docsShell.ReaderDetails.stripem2.feature1',
    description: 'Reader stripem2. Feature #1',
    defaultMessage:
      'Pick from a suite of accessories to enable countertop or roaming checkout experiences',
  },
  stripem2Feature2: {
    id: 'docsShell.ReaderDetails.stripem2.feature2',
    description: 'Reader stripem2. Feature #2',
    defaultMessage: 'Works with iOS and Android SDKs',
  },
  stripem2Feature3: {
    id: 'docsShell.ReaderDetails.stripem2.feature3',
    description: 'Reader stripem2. Feature #3',
    defaultMessage:
      'PCI certified and P2PE enabled, optimized for mobile checkout experiences',
  },
  stripem2Feature4: {
    id: 'docsShell.ReaderDetails.stripem2.feature4',
    description: 'Reader stripem2. Feature #4',
    defaultMessage: 'Accepts contactless, chip, and swipe payments',
  },
  wisepad3Name: {
    id: 'docsShell.ReaderDetails.wisepad3.name',
    description: 'Reader wisepad3. Name',
    defaultMessage: 'BBPOS WisePad™ 3',
  },
  wisepad3Description: {
    id: 'docsShell.ReaderDetails.wisepad3.description',
    description: 'Reader wisepad3. Description',
    defaultMessage:
      'The handheld, Bluetooth reader with customer-facing screen that connects to iOS and Android applications.',
  },
  wisepad3Feature1: {
    id: 'docsShell.ReaderDetails.wisepad3.feature1',
    description: 'Reader wisepad3. Feature #1',
    defaultMessage:
      'Create a roaming cashier experience, where customers can pay anywhere',
  },
  wisepad3Feature2: {
    id: 'docsShell.ReaderDetails.wisepad3.feature2',
    description: 'Reader wisepad3. Feature #2',
    defaultMessage: 'Works with the iOS and Android SDKs',
  },
  wisepad3Feature3: {
    id: 'docsShell.ReaderDetails.wisepad3.feature3',
    description: 'Reader wisepad3. Feature #3',
    defaultMessage: 'Uses Bluetooth, optimized for mobile checkout experiences',
  },
  wisepad3Feature4: {
    id: 'docsShell.ReaderDetails.wisepad3.feature4',
    description: 'Reader wisepad3. Feature #4',
    defaultMessage: 'Accepts contactless and chip payment methods',
  },
  wiseposeName: {
    id: 'docsShell.ReaderDetails.wisepose.name',
    description: 'Reader wisepose. Name',
    defaultMessage: 'BBPOS WisePOS® E',
  },
  wiseposeDescription: {
    id: 'docsShell.ReaderDetails.wisepose.description',
    description: 'Reader wisepose. Description',
    defaultMessage:
      'The hybrid countertop and handheld smart reader for JavaScript, iOS, and Android applications',
  },
  wiseposeFeature1: {
    id: 'docsShell.ReaderDetails.wisepose.feature1',
    description: 'Reader wisepose. Feature #1',
    defaultMessage: 'Customizable customer-facing display',
  },
  wiseposeFeature2: {
    id: 'docsShell.ReaderDetails.wisepose.feature2',
    description: 'Reader wisepose. Feature #2',
    defaultMessage: 'Works with the JavaScript, iOS, and Android SDKs',
  },
  wiseposeFeature3: {
    id: 'docsShell.ReaderDetails.wisepose.feature3',
    description: 'Reader wisepose. Feature #3',
    defaultMessage:
      'Requires WiFi or Ethernet, best for countertop and roaming in-store checkouts',
  },
  wiseposeFeature4: {
    id: 'docsShell.ReaderDetails.wisepose.feature4',
    description: 'Reader wisepose. Feature #4',
    defaultMessage: 'Accepts contactless, chip, and swipe payment methods',
  },
});

type CompatibilityData = $PropertyType<TerminalContext, 'compatibility_data'>;
type Shapes = $PropertyType<$Values<CompatibilityData>, 'shapes'>;
type ReaderId = $PropertyType<$ElementType<Shapes, 0>, 'reader'>;

export type ReaderDetails = {
  id: ReaderId,
  name: React.Node,
  description: React.Node,
  // Map of country code to price
  prices: {[string]: string},
  // Currency to use if the reader isn't priced in the user's country
  fallbackPriceCountry?: string,
  image: string,
  badge?: {color: 'gray' | 'green', label: React.Node},
  features: Array<React.Node>,
};

export const allReaderDetails: Array<ReaderDetails> = [
  {
    id: 'chipper',
    name: <FormattedMessage {...messages.chipperName} />,
    description: <FormattedMessage {...messages.chipperDescription} />,
    image: ChipperPhoto,
    prices: {US: '59 USD'},
    fallbackPriceCountry: 'US',
    badge: {
      color: 'gray',
      label: <FormattedMessage {...messages.chipperBadgeLabel} />,
    },
    features: [
      <FormattedMessage {...messages.chipperFeature1} />,
      <FormattedMessage {...messages.chipperFeature2} />,
      <FormattedMessage {...messages.chipperFeature3} />,
      <FormattedMessage {...messages.chipperFeature4} />,
    ],
  },
  {
    id: 'stripe_m2',
    name: <FormattedMessage {...messages.stripem2Name} />,
    description: <FormattedMessage {...messages.stripem2Description} />,
    image: StripeM2Photo,
    prices: {US: '59 USD'},
    fallbackPriceCountry: 'US',
    badge: {
      color: 'gray',
      label: <FormattedMessage {...messages.stripem2BadgeLabel} />,
    },
    features: [
      <FormattedMessage {...messages.stripem2Feature1} />,
      <FormattedMessage {...messages.stripem2Feature2} />,
      <FormattedMessage {...messages.stripem2Feature3} />,
      <FormattedMessage {...messages.stripem2Feature4} />,
    ],
  },
  {
    id: 'wisepad3',
    name: <FormattedMessage {...messages.wisepad3Name} />,
    description: <FormattedMessage {...messages.wisepad3Description} />,
    image: WisePad3Photo,
    prices: {
      AT: '59 EUR',
      AU: '89 AUD',
      BE: '59 EUR',
      CA: '79 CAD',
      DE: '59 EUR',
      DK: '439 DKK',
      ES: '59 EUR',
      FR: '59 EUR',
      GB: '49 GBP',
      IE: '59 EUR',
      NL: '59 EUR',
      NZ: '99 NZD',
      SE: '627 SEK',
      SG: '89 SGD',
    },
    features: [
      <FormattedMessage {...messages.wisepad3Feature1} />,
      <FormattedMessage {...messages.wisepad3Feature2} />,
      <FormattedMessage {...messages.wisepad3Feature3} />,
      <FormattedMessage {...messages.wisepad3Feature4} />,
    ],
  },
  {
    id: 'wisepos_e',
    name: <FormattedMessage {...messages.wiseposeName} />,
    description: <FormattedMessage {...messages.wiseposeDescription} />,
    image: WisePOSEPhoto,
    prices: {
      AT: '199 EUR',
      AU: '329 AUD',
      BE: '199 EUR',
      CA: '299 CAD',
      DE: '199 EUR',
      DK: '1481 DKK',
      ES: '199 EUR',
      FR: '199 EUR',
      GB: '179 GBP',
      IE: '199 EUR',
      NL: '199 EUR',
      NZ: '349 NZD',
      SE: '2113 SEK',
      SG: '299 SGD',
      US: '249 USD',
    },
    features: [
      <FormattedMessage {...messages.wiseposeFeature1} />,
      <FormattedMessage {...messages.wiseposeFeature2} />,
      <FormattedMessage {...messages.wiseposeFeature3} />,
      <FormattedMessage {...messages.wiseposeFeature4} />,
    ],
  },
];
